/**
 * Video Format Utilities
 *
 * Single source of truth for supported video MIME types, file extensions,
 * and the mapping between them used for upload acceptance + Firebase Storage
 * content-type resolution.
 */

export interface VideoContentTypeInfo {
  contentType: string;
  fileExtension: string;
}

export const MIME_TO_CONTENT_TYPE: Record<string, VideoContentTypeInfo> = {
  'video/mp4': { contentType: 'video/mp4', fileExtension: 'mp4' },
  'video/quicktime': { contentType: 'video/quicktime', fileExtension: 'mov' },
  'video/webm': { contentType: 'video/webm', fileExtension: 'webm' },
  'video/x-msvideo': { contentType: 'video/x-msvideo', fileExtension: 'avi' },
  'video/x-matroska': { contentType: 'video/x-matroska', fileExtension: 'mkv' },
};

export const EXTENSION_TO_CONTENT_TYPE: Record<string, VideoContentTypeInfo> = {
  mp4: MIME_TO_CONTENT_TYPE['video/mp4'],
  mov: MIME_TO_CONTENT_TYPE['video/quicktime'],
  webm: MIME_TO_CONTENT_TYPE['video/webm'],
  avi: MIME_TO_CONTENT_TYPE['video/x-msvideo'],
  mkv: MIME_TO_CONTENT_TYPE['video/x-matroska'],
};

export const SUPPORTED_VIDEO_MIME_TYPES: readonly string[] = Object.keys(MIME_TO_CONTENT_TYPE);

export const SUPPORTED_VIDEO_ACCEPT = SUPPORTED_VIDEO_MIME_TYPES.join(',');

/**
 * MIME types that browsers can decode via native HTML5 <video>.
 * AVI and Matroska are accepted for upload (transcoded server-side) but cannot be previewed client-side.
 */
export const NATIVELY_PLAYABLE_VIDEO_MIME_TYPES: readonly string[] = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
];

/**
 * Image MIME types accepted for upload. Required because the chat content
 * flow accepts images as well as videos (Flutter fires `transcodeChatImage`
 * for `media.type == 0` — see `cloud_functions_service.dart:168-169`). HEIC/
 * HEIF are accepted for upload (transcoded server-side) even though most
 * browsers can't decode them client-side.
 *
 * Note: Flutter does NOT preserve per-format image content types — it collapses
 * every image to `image/jpeg` / `jpg` (`lib/services/video_process_service.dart:17-20`).
 * Web intentionally preserves the source format via this per-format mapping.
 */
export const IMAGE_MIME_TO_CONTENT_TYPE: Record<string, VideoContentTypeInfo> = {
  'image/jpeg': { contentType: 'image/jpeg', fileExtension: 'jpg' },
  'image/png': { contentType: 'image/png', fileExtension: 'png' },
  'image/webp': { contentType: 'image/webp', fileExtension: 'webp' },
  'image/heic': { contentType: 'image/heic', fileExtension: 'heic' },
  'image/heif': { contentType: 'image/heif', fileExtension: 'heif' },
};

export const IMAGE_EXTENSION_TO_CONTENT_TYPE: Record<string, VideoContentTypeInfo> = {
  jpg: IMAGE_MIME_TO_CONTENT_TYPE['image/jpeg'],
  jpeg: IMAGE_MIME_TO_CONTENT_TYPE['image/jpeg'],
  png: IMAGE_MIME_TO_CONTENT_TYPE['image/png'],
  webp: IMAGE_MIME_TO_CONTENT_TYPE['image/webp'],
  heic: IMAGE_MIME_TO_CONTENT_TYPE['image/heic'],
  heif: IMAGE_MIME_TO_CONTENT_TYPE['image/heif'],
};

export const SUPPORTED_IMAGE_MIME_TYPES: readonly string[] = Object.keys(IMAGE_MIME_TO_CONTENT_TYPE);

export const SUPPORTED_IMAGE_ACCEPT = SUPPORTED_IMAGE_MIME_TYPES.join(',');

/**
 * Image formats the FROZEN community transcode CF (transcode_community_message_image.ts,
 * stock ffmpeg via ffmpeg-static@5.x) can decode from a RAW upload. Unlike the chat flow
 * (client-side re-encode to JPEG before upload), community uploads the source file as-is
 * (uploadFileInfoBuilder.ts source-format-preserving divergence), so HEIC/HEIF
 * (browser-decodable on Safari but not ffmpeg-5/6-decodable — usable HEIF image demuxing
 * only landed in ffmpeg 7.x) are excluded to avoid permanently-stuck "Processing" messages.
 */
export const SUPPORTED_COMMUNITY_IMAGE_MIME_TYPES: readonly string[] = [
  'image/jpeg',
  'image/png',
  'image/webp',
];

export const SUPPORTED_COMMUNITY_IMAGE_ACCEPT = SUPPORTED_COMMUNITY_IMAGE_MIME_TYPES.join(',');
